import type { ExplorerFilters } from "./client";

/**
 * Query-string helpers for the dashboard client.
 *
 * The explorer's filter set is accepted by five endpoints — list, histogram,
 * facets, count and grouped — and was serialized five times by hand. Adding a
 * filter meant editing all five, and forgetting one was a filter that silently
 * did nothing on that endpoint.
 */

/**
 * Filters whose query parameter is their field name and whose value is the
 * field value. `siteIds` is the exception and is handled separately.
 */
const PASSTHROUGH_FILTERS = [
  "directive",
  "feature",
  "blockedUrl",
  "blockedOrigin",
  "destination",
  "effectivePolicy",
  "otherOrigin",
  "sourceFile",
  "blockedHost",
  "disposition",
  "documentHost",
  "browserName",
  "osName",
  "deviceType",
  "startTime",
  "endTime",
  "pageScope",
] as const;

/**
 * Serialize the explorer filter set.
 *
 * Empty and absent filters are left out rather than sent blank, so an unset
 * filter is not the same as one set to "". Callers add their own endpoint
 * specific parameters (paging, bucket size, grouping) to the result.
 */
export function explorerFilterParams(
  filters: ExplorerFilters,
): URLSearchParams {
  const params = new URLSearchParams();

  // The only filter whose wire form differs from its field: the API takes one
  // comma-separated `siteId`, not repeated parameters.
  if (filters.siteIds?.length) {
    params.set("siteId", filters.siteIds.join(","));
  }

  // CSP is the default, so a CSP view's URLs stay as they were (task 312).
  if (filters.reportType && filters.reportType !== "csp-violation") {
    params.set("reportType", filters.reportType);
  }

  for (const name of PASSTHROUGH_FILTERS) {
    const value = filters[name];
    if (value) {
      params.set(name, value);
    }
  }

  return params;
}

/** Join a path to its query string, omitting the `?` when there is none. */
export function withQuery(path: string, params: URLSearchParams): string {
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}
